import type {
  BranchCategory,
  BranchChangesets,
  LayerTypesVersionId,
} from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryBranchesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryBranchesService'
import type { ConfigurableServer, Optional } from '@festina-life/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import {
  allConciseBranchesKeyPrefix,
  fetchConciseBranchKeyPrefix,
} from '../keys/configuration'
import { useImportBranchChangesets } from './useImportBranchChangesets'

export const BranchCategories: BranchCategory[] = [
  'main',
  'feature',
  'release',
  'none',
]

type InputArguments = {
  server: ConfigurableServer
  branchName: string
  baseBranchName?: Optional<string>
  followsBranchName?: Optional<string>
  typeVersions?: Optional<LayerTypesVersionId[]>
  configurationData?: Optional<BranchChangesets>
  category: BranchCategory
}

export const useCreateOrUpdateConciseBranch = () => {
  const client = useQueryClient()
  const { mutateAsync: importChangesets } = useImportBranchChangesets()

  return useMutation({
    mutationFn: async ({
      server,
      branchName,
      baseBranchName,
      followsBranchName,
      configurationData,
      typeVersions,
      category,
    }: InputArguments) => {
      await ConfigurationRepositoryBranchesService.branchServicePutPutBranch({
        componentName: server.toLocaleLowerCase(),
        branchName,
        requestBody: {
          _type: 'PutBranchSpecification',
          baseBranchName,
          followsBranchName,
          typesVersionIds: typeVersions,
          branchCategory: category,
        },
      })

      if (configurationData) {
        await importChangesets({ server, branchName, configurationData })
      }
    },
    onSuccess: (_, variables) =>
      client.invalidateQueries({
        queryKey: fetchConciseBranchKeyPrefix({
          server: variables.server,
          branchName: variables.branchName,
        }),
      }),
  })
}

export const useInvalidateAllConciseBranches = () => {
  const queryClient = useQueryClient()

  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [allConciseBranchesKeyPrefix],
    })
  }, [queryClient])
}
